/**
 * Mini Stock Reconciliation Quick Action Component
 * 
 * Stock Reconciliation creation form for quick actions from Item detail page.
 * 
 * Features:
 * - Purpose is locked (pre-selected, not editable)
 * - Single item only (itemCode is known from context)
 * - Warehouse selection (can be locked if from location card)
 * - Qty and Valuation Rate inputs
 * - Creates Draft Stock Reconciliation and navigates to detail page
 * 
 * IMPORTANT: All fieldnames must be verified in Stock Reconciliation and Stock Reconciliation Item meta.
 * Do not guess fieldnames - only render fields that exist.
 */

import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Loader2, 
  Warehouse, 
  Package, 
  CheckCircle2,
  FileText,
  Calculator,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { erp } from "@/lib/erp";
import { buildDocRoute } from "@/lib/config/docRoutes";
import { LinkFieldSearch } from "@/components/filter-builder/LinkFieldSearch";
import { useLanguage } from "@/i18n/useLanguage";
import { getUITranslation } from "@/lib/views/labels/translations";

interface MiniStockReconciliationQuickActionProps {
  itemCode: string;
  warehouseContext: string;
  purposeLocked: "Opening Stock" | "Stock Reconciliation";
  onDone: () => void;
  lockWarehouses?: boolean; // If false, allow full warehouse selection (no locked warehouses)
}

export function MiniStockReconciliationQuickAction({
  itemCode,
  warehouseContext,
  purposeLocked,
  onDone,
  lockWarehouses = true, // Default to true for backward compatibility
}: MiniStockReconciliationQuickActionProps) {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const ui = useMemo(() => {
    const t = (key: string, fallback: string) => getUITranslation(key, language) || fallback;
    return {
      selectWarehouseEllipsis: t("select_warehouse_ellipsis", "Select warehouse..."),
      selectWarehouse: t("select_warehouse", "Select warehouse"),
      selectedWarehouse: t("selected_warehouse", "Selected warehouse"),
      warehouse: t("warehouse", "Warehouse"),
      defaultWarehouse: t("default_warehouse", "Default Warehouse"),
      itemDetails: t("item_details", "Item Details"),
      quantity: t("quantity", "Quantity"),
      valuationRate: t("valuation_rate", "Valuation Rate"),
      createDraftStockReconciliation: t(
        "create_draft_stock_reconciliation",
        "Create Draft Stock Reconciliation"
      ),
    };
  }, [language]);

  // Form state
  const [qty, setQty] = useState<number>(1);
  const [valuationRate, setValuationRate] = useState<number>(0);
  const [userTouchedRate, setUserTouchedRate] = useState(false);
  
  // Warehouse - can be locked if from location card
  const shouldLockWarehouse = lockWarehouses && !!warehouseContext;
  const [warehouse, setWarehouse] = useState<string>(shouldLockWarehouse ? warehouseContext : "");

  // Fetch Stock Reconciliation meta to verify fields exist
  const { data: stockReconciliationMeta } = useQuery({
    queryKey: ["meta", "Stock Reconciliation"],
    queryFn: () => erp.meta.getDocTypeSchema("Stock Reconciliation"),
  });

  // Fetch Stock Reconciliation Item meta to verify child table fieldnames
  const { data: stockReconciliationItemMeta } = useQuery({
    queryKey: ["meta", "Stock Reconciliation Item"],
    queryFn: () => erp.meta.getDocTypeSchema("Stock Reconciliation Item"),
    enabled: !!stockReconciliationMeta, // Only fetch if parent meta exists
  });

  // Fetch Warehouse meta to build filters matching core Frappe behavior
  const { data: warehouseMeta } = useQuery({
    queryKey: ["meta", "Warehouse"],
    queryFn: () => erp.meta.getDocTypeSchema("Warehouse"),
  });

  // Fetch the context warehouse to get its company (for filtering other warehouses)
  const { data: contextWarehouseDoc } = useQuery({
    queryKey: ["warehouse-detail", warehouseContext],
    queryFn: async () => {
      if (!warehouseContext) return null;
      const client = await import("@/lib/erp/client").then(m => m.getERPClient());
      return client.get<Record<string, any>>({ doctype: "Warehouse", name: warehouseContext });
    },
    enabled: !!warehouseContext,
  });

  // Extract company from context warehouse (if available)
  const contextCompany = contextWarehouseDoc?.company as string | undefined;

  // Check if this is Opening Stock or Stock Reconciliation for icon selection
  const isOpeningStock = purposeLocked === "Opening Stock";
  const PurposeIcon = isOpeningStock ? FileText : Calculator;

  // State for image loading error
  const [imageError, setImageError] = useState(false);

  // Fetch item's primary image and name for visualization
  const { data: itemImage } = useQuery({
    queryKey: ["item-primary-image", itemCode],
    queryFn: async () => {
      if (!itemCode) return null;
      try {
        const frappe = await import("@/lib/frappe-client").then(m => m.frappe);
        // Fetch item gallery to get primary image
        const galleryData = await frappe.call<{
          item_code: string;
          media: Array<{
            name: string;
            url: string;
            alt: string;
            type: 'image' | 'video';
            isPrimaryImage: boolean;
            isPrimaryVideo: boolean;
            idx: number;
            thumbnailUrl: string | null;
          }>;
          primary_image: string | null;
          primary_video: string | null;
          schema_version: string;
        }>('zaviago_backend.api.item_gallery.get_item_gallery', {
          item_code: itemCode,
          contract: 'v2',
        });
        
        // Use primary_image from response, or find first image from media
        const primaryImageUrl = galleryData?.primary_image || 
          galleryData?.media?.find(item => item.type === 'image')?.url;
        
        if (primaryImageUrl) {
          const { normalizeFileUrl } = await import("@/lib/attachments/utils");
          return normalizeFileUrl(primaryImageUrl);
        }
        return null;
      } catch (error) {
        console.error("[MiniStockReconciliationQuickAction] Failed to fetch item image:", error);
        return null;
      }
    },
    enabled: !!itemCode,
  });

  // Fetch item document to get item name
  const { data: itemDoc } = useQuery({
    queryKey: ["item-detail", itemCode],
    queryFn: async () => {
      if (!itemCode) return null;
      try {
        return await erp.items.detail(itemCode);
      } catch (error) {
        console.error("[MiniStockReconciliationQuickAction] Failed to fetch item:", error);
        return null;
      }
    },
    enabled: !!itemCode,
  });

  const itemName = itemDoc?.item_name as string | undefined;

  // Reset pricing state when item changes (avoid showing stale rate)
  useEffect(() => {
    setValuationRate(0);
    setUserTouchedRate(false);
  }, [itemCode]);

  // Fetch ERPNext-computed valuation rate (same defaulting logic as Stock Reconciliation form)
  const valuationRateQuery = useQuery<{ valuation_rate: number } | null>({
    queryKey: ["stock-reco-valuation-rate", itemCode, warehouse, contextCompany || null],
    queryFn: async () => {
      if (!itemCode || !warehouse) return null;
      try {
        const result = await erp.stockReconciliations.getQuickActionValuationRate({
          item_code: itemCode,
          warehouse,
          company: contextCompany,
        });
        // Temporary debug - helps confirm backend is returning expected valuation rate (e.g. 99)
        console.log(
          `[MiniStockReconciliationQuickAction] Valuation rate for item="${itemCode}" warehouse="${warehouse}":`,
          result
        );
        return result;
      } catch (error) {
        console.error("[MiniStockReconciliationQuickAction] Failed to fetch valuation rate:", error);
        return null;
      }
    },
    enabled: !!itemCode && !!warehouse && !userTouchedRate,
    staleTime: 0,
    gcTime: 0,
  });

  // Prefill valuation rate only if user hasn't manually edited it
  useEffect(() => {
    if (userTouchedRate) return;
    const suggested = valuationRateQuery.data?.valuation_rate;
    if (typeof suggested === "number" && suggested > 0) {
      setValuationRate(suggested);
    }
  }, [valuationRateQuery.data, userTouchedRate]);

  // Build warehouse filters matching core Frappe behavior (meta-gated)
  const warehouseDefaultFilters = useMemo(() => {
    if (!warehouseMeta || !contextCompany) return undefined;
    
    const filters: any[] = [];
    
    // Filter by company if warehouse has company field
    const hasCompanyField = warehouseMeta.fields.some(f => f.fieldname === "company");
    if (hasCompanyField) {
      filters.push(["company", "=", contextCompany]);
    }
    
    // Exclude group warehouses if meta confirms is_group field exists
    const hasIsGroupField = warehouseMeta.fields.some(f => f.fieldname === "is_group");
    if (hasIsGroupField) {
      filters.push(["is_group", "=", 0]);
    }
    
    return filters.length > 0 ? filters : undefined;
  }, [warehouseMeta, contextCompany]);

  // Check which fields exist in meta (for conditional rendering)
  const hasSetWarehouse = useMemo(
    () => stockReconciliationMeta?.fields.some(f => f.fieldname === "set_warehouse") ?? false,
    [stockReconciliationMeta]
  );

  // Child table fieldnames check
  const childTableFieldnames = useMemo(() => {
    if (!stockReconciliationItemMeta) return {};
    const fieldnames = new Set(stockReconciliationItemMeta.fields.map(f => f.fieldname));
    return {
      item_code: fieldnames.has("item_code"),
      warehouse: fieldnames.has("warehouse"),
      qty: fieldnames.has("qty"),
      valuation_rate: fieldnames.has("valuation_rate"),
    };
  }, [stockReconciliationItemMeta]);

  // Validation
  const canSubmit = useMemo(() => {
    if (qty <= 0) return false;
    if (!warehouse) return false;
    return true;
  }, [qty, warehouse]);

  // Create Draft Stock Reconciliation mutation
  const createMutation = useMutation({
    mutationFn: async (payload: Record<string, any>) => {
      return erp.stockReconciliations.create(payload);
    },
    onSuccess: (created) => {
      // Navigate to created Stock Reconciliation detail page
      if (created.name) {
        navigate(buildDocRoute("Stock Reconciliation", created.name));
      }
      onDone();
    },
    onError: (error) => {
      console.error("[MiniStockReconciliationQuickAction] Failed to create Stock Reconciliation:", error);
      // TODO: Show error toast/alert
    },
  });

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!canSubmit || !stockReconciliationItemMeta) return;

    // Build minimal payload - backend validates everything
    // Only use fieldnames that we verified exist in meta
    const itemsRow: Record<string, any> = {};

    // Add verified child table fields
    if (childTableFieldnames.item_code) {
      itemsRow.item_code = itemCode;
    }
    if (childTableFieldnames.warehouse) {
      itemsRow.warehouse = warehouse;
    }
    if (childTableFieldnames.qty) {
      itemsRow.qty = qty;
    }
    if (childTableFieldnames.valuation_rate && valuationRate > 0) {
      itemsRow.valuation_rate = valuationRate;
    }

    const payload: Record<string, any> = {
      purpose: purposeLocked,
      docstatus: 0, // Draft
      items: [itemsRow],
    };

    // Add header warehouse field (meta-gated)
    if (hasSetWarehouse && warehouse) {
      payload.set_warehouse = warehouse;
    }

    // Log payload before mutation for debugging
    console.log("[MiniStockReconciliationQuickAction] Submitting payload:", JSON.stringify(payload, null, 2));

    createMutation.mutate(payload);
  };

  // Build field groups for rendering
  const fieldGroups = useMemo(() => {
    const groups: Array<{
      label: string;
      icon?: React.ReactNode;
      fields: Array<{
        key: string;
        label: string;
        element: React.ReactNode;
      }>;
    }> = [];

    // Group 1: Warehouse (if not locked and field exists)
    const warehouseFields: Array<{
      key: string;
      label: string;
      element: React.ReactNode;
    }> = [];

    if (hasSetWarehouse && !shouldLockWarehouse) {
      warehouseFields.push({
        key: "set_warehouse",
        label: ui.defaultWarehouse,
        element: (
          <div className="flex items-center gap-2 flex-1 max-w-[60%] justify-end">
            <LinkFieldSearch
              doctype="Warehouse"
              referenceDoctype="Stock Reconciliation"
              value={warehouse || null}
              onChange={(value) => setWarehouse(value || "")}
              placeholder={ui.selectWarehouseEllipsis}
              className="flex-1 !bg-transparent border-0 p-0 text-[17px] text-gray-900 dark:text-gray-100 font-normal focus:outline-none focus:ring-0"
              data-field="set_warehouse"
              defaultFilters={warehouseDefaultFilters}
            />
          </div>
        ),
      });
    }

    if (warehouseFields.length > 0) {
      const hasWarehouse = !!warehouse;
      groups.push({
        label: hasWarehouse ? ui.selectedWarehouse : ui.selectWarehouse,
        icon: undefined,
        fields: warehouseFields,
      });
    }

    // Group 2: Item Details
    groups.push({
      label: ui.itemDetails,
      icon: undefined,
      fields: [
        {
          key: "qty",
          label: ui.quantity,
          element: (
            <Input
              type="number"
              min={0}
              step="0.01"
              value={qty}
              onChange={(e) => setQty(Number(e.target.value))}
              className="flex-1 max-w-[60%] text-right !bg-transparent border-0 p-0 text-[17px] text-gray-900 dark:text-gray-100 font-normal focus:outline-none focus:ring-0"
            />
          ),
        },
        {
          key: "valuation_rate",
          label: ui.valuationRate,
          element: (
            <Input
              type="number"
              min={0}
              step="0.01"
              value={valuationRate}
              onChange={(e) => {
                setUserTouchedRate(true);
                setValuationRate(Number(e.target.value));
              }}
              className="flex-1 max-w-[60%] text-right !bg-transparent border-0 p-0 text-[17px] text-gray-900 dark:text-gray-100 font-normal focus:outline-none focus:ring-0"
              placeholder="0.00"
            />
          ),
        },
      ],
    });

    return groups;
  }, [warehouse, qty, valuationRate, hasSetWarehouse, shouldLockWarehouse, warehouseDefaultFilters, ui]);

  return (
    <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
      <div className="flex-1 overflow-y-auto relative will-change-[scroll-position]">
        {/* Visual Flow - Apple Style with Product Image */}
        <div className="mb-6 px-10">
          <div className="bg-gray-50/50 dark:bg-gray-900/50 rounded-lg p-6 border border-gray-200/30 dark:border-gray-800/30">
            {/* Product Image → Purpose Icon → Warehouse Flow */}
            <div className="flex flex-col gap-3">
              {/* Main Flow Row - Relative for absolute centering */}
              <div className="relative flex items-center justify-between min-h-[80px]">
                {/* Product Image with Quantity Badge */}
                <div className="flex-shrink-0 flex items-center gap-3">
                  <div className="relative flex items-center justify-center">
                    {itemImage && !imageError ? (
                      <div className="w-20 h-20 rounded-md bg-white dark:bg-gray-800 border border-gray-200/50 dark:border-gray-700/50 overflow-hidden shadow-sm">
                        <img
                          src={itemImage}
                          alt="Product"
                          className="w-full h-full object-cover"
                          onError={() => setImageError(true)}
                        />
                        {/* Quantity Badge - Apple Style Yellow */}
                        <div className="absolute -top-1.5 -right-1.5 min-w-[28px] h-7 px-1.5 rounded-full bg-gradient-to-b from-[#FFD60A] to-[#FFC107] flex items-center justify-center shadow-lg border-2 border-white dark:border-gray-900 ring-1 ring-black/5">
                          <span className="text-[13px] font-semibold text-gray-900 leading-none tracking-tight">
                            {qty > 99 ? '99+' : qty}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="w-20 h-20 rounded-md bg-white dark:bg-gray-800 border border-gray-200/50 dark:border-gray-700/50 flex items-center justify-center shadow-sm relative">
                        <Package className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                        {/* Quantity Badge - Apple Style Yellow */}
                        <div className="absolute -top-1.5 -right-1.5 min-w-[28px] h-7 px-1.5 rounded-full bg-gradient-to-b from-[#FFD60A] to-[#FFC107] flex items-center justify-center shadow-lg border-2 border-white dark:border-gray-900 ring-1 ring-black/5">
                          <span className="text-[13px] font-semibold text-gray-900 leading-none tracking-tight">
                            {qty > 99 ? '99+' : qty}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Product Name */}
                  {itemName && (
                    <div className="text-left">
                      <div className="text-[13px] font-semibold text-gray-900 dark:text-gray-100 leading-tight max-w-[140px] truncate">
                        {itemName}
                      </div>
                    </div>
                  )}
                </div>

                {/* Purpose Icon - Always centered */}
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center z-10">
                  <PurposeIcon className={cn(
                    "w-7 h-7",
                    isOpeningStock ? "text-[#34C759]" : "text-[#007AFF]"
                  )} />
                </div>

                {/* Warehouse Display */}
                {warehouse ? (
                  <div className="flex-shrink-0 flex items-center gap-3">
                    <div className="w-12 h-12 rounded bg-white dark:bg-gray-800 flex items-center justify-center border border-gray-200/50 dark:border-gray-700/50 shadow-sm flex-shrink-0">
                      <Warehouse className="w-6 h-6 text-[#007AFF]" />
                    </div>
                    <div className="text-left">
                      <div className="text-[12px] font-medium text-gray-500 dark:text-gray-400 mb-1">
                        {shouldLockWarehouse ? ui.selectedWarehouse : ui.warehouse}
                      </div>
                      <div className="text-[16px] font-semibold text-gray-900 dark:text-gray-100">
                        {warehouse}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex-shrink-0 flex items-center gap-3">
                    <div className="w-12 h-12 rounded bg-white dark:bg-gray-800 flex items-center justify-center border border-gray-200/50 dark:border-gray-700/50 shadow-sm flex-shrink-0">
                      <Warehouse className="w-6 h-6 text-gray-400 dark:text-gray-500" />
                    </div>
                    <div className="text-left">
                      <div className="text-[12px] font-medium text-gray-500 dark:text-gray-400 mb-1">
                        {ui.warehouse}
                      </div>
                      <div className="text-[16px] font-semibold text-gray-400 dark:text-gray-500 italic">
                        {ui.selectWarehouseEllipsis}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="py-2 space-y-5 px-10">
          {fieldGroups.map((group) => (
            <div
              key={group.label}
              className={cn(
                "bg-gray-50 dark:bg-gray-950 rounded-md overflow-hidden border border-gray-200/40 dark:border-gray-700/40"
              )}
            >
              {/* Group Header */}
              <div className="px-5 py-2.5 border-b border-gray-200/30 dark:border-gray-700/30">
                <div className="flex items-center gap-2">
                  {group.icon && group.icon}
                  <span className="text-[13px] font-medium text-gray-600 dark:text-gray-400 tracking-[-0.1px]">
                    {group.label}
                  </span>
                </div>
              </div>
              
              {group.fields.map((field, fieldIndex) => {
                const isLastInGroup = fieldIndex === group.fields.length - 1;
                return (
                  <div
                    key={field.key}
                    className={cn(
                      "flex items-center justify-between min-h-[52px] px-5",
                      !isLastInGroup && "border-b border-gray-200/40 dark:border-gray-700/40"
                    )}
                  >
                    <label className="text-[17px] text-gray-900 dark:text-gray-100 font-normal flex-shrink-0">
                      {field.label}
                      {(field.key === "set_warehouse" || field.key === "qty") && (
                        <span className="text-destructive ml-1">*</span>
                      )}
                    </label>
                    <div className="flex-1 max-w-[60%] flex justify-end">
                      {field.element}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Bottom fade-out gradient */}
        <div className="sticky bottom-0 left-0 right-0 z-10 h-24 bg-gradient-to-t from-white via-white/95 to-transparent dark:from-gray-900 dark:via-gray-900/95 pointer-events-none will-change-[opacity]" />
      </div>

      {/* Sticky Footer Button */}
      <div className="flex justify-center px-10 py-6 bg-white dark:bg-gray-900 border-t border-gray-200/50 dark:border-gray-800/50 flex-shrink-0">
        <Button
          type="submit"
          disabled={!canSubmit || createMutation.isPending}
          className={cn(
            "w-full text-[17px] font-semibold text-white px-7 py-3.5 h-auto rounded disabled:opacity-50 disabled:cursor-not-allowed transition-all",
            "bg-[#007AFF] hover:bg-[#0051D5] active:bg-[#0040B3] shadow-sm hover:shadow-md"
          )}
        >
          {createMutation.isPending ? (
            <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Creating...
            </>
          ) : (
            <div className="flex items-center justify-center gap-2">
              <CheckCircle2 className="w-5 h-5" />
              <span>{ui.createDraftStockReconciliation}</span>
            </div>
          )}
        </Button>
      </div>
    </form>
  );
}

